import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
    Eye, EyeOff, ArrowRight, Check, X, Loader2,
    Building2, Mail, User as UserIcon, Briefcase, Info,
    ShieldCheck, Gift, MailCheck, RefreshCw, ArrowLeft,
    CreditCard, Zap, TrendingUp, Users as UsersIcon, PieChart,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { useBranding, getOrgUrlHints } from "@/lib/branding";
import { readAuthHero, writeAuthHero } from "@/lib/auth-hero-cache";
import { applyBranding } from "@/lib/settings-context";
import { captureLoginSnapshot } from "@/lib/theme-snapshot";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { COUNTRY_DIAL_CODES, findCountry } from "@/lib/country-dial-codes";
import BrandHeroImage from "@/components/BrandHeroImage";

// ─── Clean dial-code pill badge ─────────────────────────────────────────────
// Renders the dial code (e.g. "+91") as a monospace pill. We deliberately
// avoid 🇺🇸-style flag emojis: they fall back to plain text on Windows /
// Linux without an emoji font. A dial-code pill is universally legible
// and also more informative — users actually need the dial code to type
// a phone number, not the flag glyph.
function DialBadge({ dial, className = "" }) {
    return (
        <span
            className={
                "inline-flex items-center justify-center h-5 min-w-[2.25rem] " +
                "px-1.5 rounded-sm border border-border bg-muted/40 " +
                "font-mono text-[10px] leading-none tracking-tight text-foreground " +
                className
            }
        >
            {dial}
        </span>
    );
}

// ─── Live password strength heuristic ───────────────────────────────────────
function passwordChecks(pw, email, minLen, requiredClasses) {
    const checks = [
      { id: "len", label: `At least ${minLen} characters`, ok: (pw || "").length >= minLen },
        { id: "lower", label: "Lowercase letter", ok: /[a-z]/.test(pw || "") },
        { id: "upper", label: "Uppercase letter", ok: /[A-Z]/.test(pw || "") },
        { id: "digit", label: "Digit", ok: /[0-9]/.test(pw || "") },
        { id: "symbol", label: "Symbol", ok: /[^a-zA-Z0-9]/.test(pw || "") },
    ];
    if (email) {
        const local = (email.split("@")[0] || "").toLowerCase();
        checks.push({
            id: "not-email", label: "Different from your email",
            ok: !!(pw && local && pw.toLowerCase() !== local),
        });
    }
    const classCount = checks.filter((c) => ["lower", "upper", "digit", "symbol"].includes(c.id) && c.ok).length;
    const lenOk = checks.find((c) => c.id === "len")?.ok;
    const strongEnough = lenOk && classCount >= requiredClasses;
    return { checks, strongEnough, classCount };
}

// iter-121 — capture captcha provider's response token via the official
// JS SDK. Loaded on demand only when SA has enabled the feature.
const CAPTCHA_SCRIPT = {
    hcaptcha: "https://js.hcaptcha.com/1/api.js?render=explicit",
    recaptcha: "https://www.google.com/recaptcha/api.js?render=explicit",
    turnstile: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
};

function CaptchaWidget({ provider, siteKey, onToken }) {
    const widgetRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        if (!provider || !siteKey) return;
        const src = CAPTCHA_SCRIPT[provider];
        if (!src) return;
        // Inject the script once.
        let s = document.querySelector(`script[src^="${src.split("?")[0]}"]`);
        const attach = () => {
            if (!widgetRef.current) return;
            try {
                if (provider === "hcaptcha" && window.hcaptcha) {
                    widgetIdRef.current = window.hcaptcha.render(widgetRef.current, {
                        sitekey: siteKey, callback: onToken,
                    });
                } else if (provider === "recaptcha" && window.grecaptcha) {
                    window.grecaptcha.ready(() => {
                        widgetIdRef.current = window.grecaptcha.render(widgetRef.current, {
                            sitekey: siteKey, callback: onToken,
                        });
                    });
                } else if (provider === "turnstile" && window.turnstile) {
                    widgetIdRef.current = window.turnstile.render(widgetRef.current, {
                        sitekey: siteKey, callback: onToken,
                    });
                }
            } catch (e) { /* ignore re-renders */ }
        };
        if (!s) {
            s = document.createElement("script");
            s.src = src;
            s.async = true;
            s.defer = true;
            s.onload = attach;
            document.body.appendChild(s);
        } else if (window.hcaptcha || window.grecaptcha || window.turnstile) {
            attach();
        } else {
            s.addEventListener("load", attach);
        }
    }, [provider, siteKey, onToken]);

    return <div ref={widgetRef} className="mt-1" data-testid="signup-captcha-widget" />;
}

export default function Signup() {
    const { user, signup } = useAuth();    // signup helper still used for legacy/JWT writeback
    const { setDefaultTheme } = useTheme();
    const platformBrand = useBranding();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    // iter-121 — capture referral URL param on mount. Promo code intentionally removed.
    const referrerUserId = sp.get("ref") || sp.get("referrer") || "";

    // Step 1: signup form
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [contact, setContact] = useState("");
    const [countryCode, setCountryCode] = useState("US");
    const [industry, setIndustry] = useState("");

    // iter-121.98 — Public branding lookup (defaults to platform-tenant
    // when no email yet). Lets the signup hero render the configured
    // login_image_url. Re-fetched whenever the email field is filled out.
    // iter-121.128 — Don't trigger tenant-specific lookup on initial
    // mount; only after the user actively edits the email field. This
    // prevents the platform branding from flashing in for ~500ms and
    // then being overwritten by a tenant override when the email field
    // is pre-filled (e.g. when arriving from /login → "Start a free
    // trial" with an email already populated).
    const [tenantBrand, setTenantBrand] = useState(null);
    // Platform DEFAULT branding (no-email /branding/lookup) — applied on mount
    // so the SA-configured logo renders immediately on /signup.
    const [platformDefaultBrand, setPlatformDefaultBrand] = useState(null);
    // Hero-flicker fix: `brandResolved` flips true once the pre-email
    // branding lookup has settled (success OR failure). Until then the hero
    // shows the last-known configured image for this site (local cache) or
    // a neutral panel — never the built-in gradient mock.
    const [brandResolved, setBrandResolved] = useState(false);
    const [cachedHero] = useState(() => readAuthHero(getOrgUrlHints()));
    const [userTouchedEmail, setUserTouchedEmail] = useState(false);
    // (1) PRE-EMAIL / platform branding — fetched ONCE on mount and never
    // cancelled by typing. Includes URL org hints (subdomain / ?org= /
    // configured Organization URL) so a shared login URL shows the right org
    // branding; the backend falls back to the platform default when
    // unmatched. Running this only once matters: the response can carry the
    // hero image inline (~1 MB), and re-firing it per keystroke delayed the
    // saved image and caused the gradient-hero flicker.
    useEffect(() => {
        let cancelled = false;
        const hints = getOrgUrlHints();
        const qs = new URLSearchParams();
        if (hints.slug) qs.set("slug", hints.slug);
        if (hints.host) qs.set("host", hints.host);
        api.get(`/branding/lookup${qs.toString() ? `?${qs.toString()}` : ""}`)
            .then((r) => {
                if (cancelled) return;
                setPlatformDefaultBrand(r.data || null);
                writeAuthHero(hints, r.data || null);
            })
            .catch(() => { /* keep cached hero / built-in fallback */ })
            .finally(() => { if (!cancelled) setBrandResolved(true); });
        return () => { cancelled = true; };
    }, []);

    // (2) Tenant override — only once the user has typed a FULL email
    // (debounced). A confirmed tenant override wins for logo + app name;
    // otherwise the platform branding from (1) stays in effect.
    useEffect(() => {
        const trimmed = (email || "").trim().toLowerCase();
        const hasFullEmail = trimmed && trimmed.includes("@") && trimmed.length >= 5;
        if (!(hasFullEmail && userTouchedEmail)) {
            setTenantBrand(null);
            return undefined;
        }
        let stale = false;
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/branding/lookup?email=${encodeURIComponent(trimmed)}`);
                if (!stale) setTenantBrand(r.data?.tenant_found ? r.data : null);
            } catch {
                if (!stale) setTenantBrand(null);
            }
        }, 500);
        return () => { stale = true; clearTimeout(t); };
    }, [email, userTouchedEmail]);

    // iter-121.130 — Push the platform/tenant branding from
    // `/branding/lookup` into the document (favicon, title, CSS vars
    // for primary/sidebar/font etc.) so the /signup page renders the
    // SA-promoted theme immediately rather than inheriting whatever
    // the previous logged-in session left on the DOM. Mirrors the
    // Login.jsx fix — pairs with `resetBrandingDom()` on logout.
    useEffect(() => {
        const publicBrand = tenantBrand || platformDefaultBrand;
        if (publicBrand) {
            applyBranding(publicBrand);
            setDefaultTheme(publicBrand.theme_mode);
            const systemDark = publicBrand.theme_mode === "system"
                && window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
            captureLoginSnapshot({
                forceDark: publicBrand.theme_mode === "dark" || systemDark,
            });
        }
    }, [tenantBrand, platformDefaultBrand, setDefaultTheme]);

    // iter-121.102 — Merge tenant/platform-default branding over platform
    // defaults so the Signup page shows the tenant/platform logo/app_name.
    const brand = useMemo(() => {
        const src = tenantBrand || platformDefaultBrand;
        const configuredName = ((src?.app_name) || platformBrand.configuredAppName || "").trim();
        return {
            ...platformBrand,
            appName: configuredName || platformBrand.appName,
            configuredName,
            logoUrl: src?.logo_url || platformBrand.logoUrl,
            tagline: src?.tagline || platformBrand.tagline,
            // iter-2026 — signup hero falls back to signup→login image, and
            // to the platform default (no-email load → platformDefaultBrand).
            signupImageUrl: src?.signup_image_url || src?.login_image_url
                // Last-known saved image while pending, or if the lookup failed.
                || ((!brandResolved || !(platformDefaultBrand?.signup_image_url || platformDefaultBrand?.login_image_url)) ? (cachedHero?.signup_image_url || cachedHero?.login_image_url || null) : null),
        };
    }, [platformBrand, tenantBrand, platformDefaultBrand, brandResolved, cachedHero]);
    // iter-121.6 — free-text industry shown when industry === "other".
    const [customIndustry, setCustomIndustry] = useState("");
    const [orgName, setOrgName] = useState("");
    const [orgNameLocked, setOrgNameLocked] = useState(false);
    const [tenantName, setTenantName] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    // iter — clicking the "I agree…" checkbox opens a mandatory Terms
    // modal (spec §1). Only after the user scrolls to the bottom + hits
    // Agree does ``acceptedTerms`` flip to true. Cancel / X keeps it
    // false so the Send Verification Code button stays disabled.
    const [tosModalOpen, setTosModalOpen] = useState(false);
    const [tosVersionId, setTosVersionId] = useState(null);
    const [captchaToken, setCaptchaToken] = useState("");
    const onCaptchaToken = useCallback((tok) => setCaptchaToken(tok || ""), []);

    // iter-121.6 — inline email-domain validation. Debounced server call
    // to /api/auth/signup/check-email so we can disable the submit button
    // BEFORE the user clicks. `null` = unchecked, true/false = checked.
    const [emailDomainOk, setEmailDomainOk] = useState(null);
    const [emailDomainReason, setEmailDomainReason] = useState("");
    const [emailChecking, setEmailChecking] = useState(false);

    // iter-121.6 — SA-uploaded Terms / Privacy PDF URLs. Falls back to
    // static /terms /privacy SPA routes until SA uploads PDFs.
    const [legalLinks, setLegalLinks] = useState({
        terms: { url: "/terms", is_pdf: false },
        privacy: { url: "/privacy", is_pdf: false },
    });

    // Step 2: OTP entry
    const [stage, setStage] = useState("form");           // 'form' | 'otp'
    const [otpCode, setOtpCode] = useState("");
    const [otpError, setOtpError] = useState("");         // inline "code didn't match" message
    const [otpCooldown, setOtpCooldown] = useState(0);
    const [otpTtlSec, setOtpTtlSec] = useState(600);
    const [demoCode, setDemoCode] = useState("");

    const [industries, setIndustries] = useState([]);
    const [policy, setPolicy] = useState({
        password_min_length: 10, password_required_classes: 3, trial_period_days: 14,
        captcha_enabled: false, captcha_provider: "hcaptcha", captcha_site_key: "",
        // iter-121.53 — phone digit bounds from /signup/policy; defaults
        // mirror the backend (exactly 10 digits — US/CA/IN national #).
        phone_min_digits: 10, phone_max_digits: 10,
    });
    const [submitting, setSubmitting] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [serverError, setServerError] = useState("");
    const [justSignedUp, setJustSignedUp] = useState(false);

    useEffect(() => {
        api.get("/auth/signup/policy").then((r) => setPolicy(r.data)).catch(() => { });
        api.get("/organizations/industries").then((r) => setIndustries(r.data?.industries || [])).catch(() => { });
        // iter-121.6 — wire Terms / Privacy links from SA-configured PDFs.
        api.get("/legal/links").then((r) => {
            if (r.data && r.data.terms && r.data.privacy) {
                setLegalLinks(r.data);
            }
        }).catch(() => { });
    }, []);

    // iter-121.6 — debounced email-domain check. Fires 350ms after the
    // user stops typing. Skipped while the local well-formedness regex
    // still fails (no point sending obvious garbage to the server).
    useEffect(() => {
        const e = (email || "").trim().toLowerCase();
        if (!/.+@.+\..+/.test(e)) {
            setEmailDomainOk(null);
            setEmailDomainReason("");
            setEmailChecking(false);
            setOrgNameLocked(false);
            return;
        }
        setEmailChecking(true);
        const handle = setTimeout(async () => {
            try {
                const r = await api.get(`/auth/signup/check-email?email=${encodeURIComponent(e)}`);
                setEmailDomainOk(!!r.data?.allowed);
                setEmailDomainReason(r.data?.reason || "");
                if (r.data?.allowed) {
                    const domainOrgRes = await api.get(`/auth/signup/domain-org?email=${encodeURIComponent(e)}`);
                    if (domainOrgRes.data?.exists) {
                        setOrgName(domainOrgRes.data.org_name || "");
                        setOrgNameLocked(true);
                    } else {
                        setOrgNameLocked(false);
                    }
                } else {
                    setOrgNameLocked(false);
                }
            } catch (_e) {
                // Network blip — don't block the user, the server-side
                // check at /signup/initiate is the source of truth.
                setEmailDomainOk(null);
                setEmailDomainReason("");
                setOrgNameLocked(false);
            } finally {
                setEmailChecking(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [email]);

    // Cooldown timer for "Resend code".
    useEffect(() => {
        if (otpCooldown <= 0) return;
        const t = setInterval(() => setOtpCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [otpCooldown]);

    const tenantPlaceholder = useMemo(() => (orgName ? `${orgName} Workspace` : "Workspace name (optional)"), [orgName]);
    const pwState = useMemo(
        () => passwordChecks(password, email, policy.password_min_length, policy.password_required_classes),
        [password, email, policy],
    );

    const captchaOk = !policy.captcha_enabled || (policy.captcha_enabled && !!captchaToken);

    // iter-2026 — single-screen signup. "Send verification code" only needs a
    // valid work email + strong password (+ captcha when enabled). The name,
    // country, contact, industry and organization-name fields are hidden and
    // optional (stored NULL / auto-derived), so they no longer gate submit.
    const canSubmit = (
        /.+@.+\..+/.test(email) &&
        emailDomainOk !== false &&
        !emailChecking &&
        pwState.strongEnough &&
        captchaOk &&
        !submitting
    );

    // Final "Verify & get started" gate — code must have been sent and a full
    // 6-digit code entered. (Terms consent moved to the Upgrade Plan flow.)
    const canVerify = (
        stage === "otp" &&
        (otpCode || "").replace(/\D/g, "").length === 6 &&
        !verifying
    );

    const initiate = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setServerError("");
        setSubmitting(true);
        try {
            const r = await api.post("/auth/signup/initiate", {
                name: name.trim(),
                email: email.trim().toLowerCase(),
                password,
                contact_number: contact.trim(),
                industry,
                custom_industry: industry === "other" ? customIndustry.trim() : undefined,
                org_name: orgName.trim(),
                // iter-2026 — Workspace/tenant name is auto-derived from the
                // email DOMAIN by the backend when omitted. Send empty (not the
                // email local-part) so onboarding shows the domain-derived org
                // name (e.g. "Examplebiz") rather than the raw local-part.
                tenant_name: (
                    (tenantName || "").trim()
                    || (orgName || "").trim()
                    || undefined
                ),
                country_code: countryCode || "US",
                accepted_terms: true,
                referrer_user_id: referrerUserId || undefined,
                captcha_token: captchaToken || undefined,
            });
            const d = r.data || {};
            setStage("otp");
            setOtpCooldown(d.cooldown_sec || 60);
            setOtpTtlSec((d.ttl_minutes || 10) * 60);
            if (d.demo_code) {
                setDemoCode(d.demo_code);
                toast.info("Email sender not configured — demo mode", {
                    description: `Use code ${d.demo_code} to continue.`,
                });
            } else {
                toast.success("Verification code sent", {
                    description: `Check your inbox at ${d.email}.`,
                });
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Sign up failed. Please try again.";
            setServerError(String(detail));
            toast.error("Sign up failed", { description: String(detail) });
        } finally {
            setSubmitting(false);
        }
    };

    const verify = async () => {
        const clean = (otpCode || "").replace(/\D/g, "");
        if (clean.length < 6) { setOtpError("Enter the 6-digit code first."); return; }
        setVerifying(true);
        setOtpError("");
        setJustSignedUp(true);
        try {
            const r = await api.post("/auth/signup/verify-email", {
                email: email.trim().toLowerCase(), code: clean,
            });
            const d = r.data || {};
            if (d.access_token && d.user) {
                // Account was created. Stash JWT + redirect.
                localStorage.setItem("analyzio_token", d.access_token);
                localStorage.setItem("analyzio_user", JSON.stringify(d.user));
                toast.success(`Welcome to ${brand.appName}!`, {
                    description: policy.trial_period_days
                        ? `Your ${policy.trial_period_days}-day free trial of ${d.tenant?.name || orgName} just started.`
                        : `Your workspace for ${d.tenant?.name || orgName} is ready.`,
                });
                // Force reload so the AuthProvider picks up the new token cleanly.
                window.location.href = "/onboarding";
            } else {
                // No account yet (e.g., pending row expired). Roll back to step 1.
                setJustSignedUp(false);
                setOtpError("We couldn't complete signup. Please request a new code and try again.");
                setStage("form");
            }
        } catch (err) {
            setJustSignedUp(false);
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status >= 500) {
                setOtpError("Something went wrong on our side. Please try again in a moment.");
            } else {
                setOtpError(String(detail || "That code didn't match. Please check and try again."));
            }
        } finally {
            setVerifying(false);
        }
    };

    const resendOtp = async () => {
        setOtpError("");
        try {
            const r = await api.post("/auth/signup/verify-email/send", { email: email.trim().toLowerCase() });
            if (r.data?.demo_code) {
                setDemoCode(r.data.demo_code);
                toast.info(`Demo code: ${r.data.demo_code}`);
            } else {
                toast.success("Code resent");
            }
            setOtpCooldown(60);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send");
            setOtpCooldown(60);
        }
    };

    if (user && !justSignedUp) return <Navigate to="/" replace />;

    return (
        <div className="min-h-screen w-full grid lg:grid-cols-[55%_45%] bg-background" data-testid="signup-page">
            {/* Left: form */}
            <div className="flex flex-col justify-center px-8 lg:px-12 xl:px-16 py-10 overflow-y-auto">
                <div className="flex items-center justify-between gap-3 mb-6" data-testid="signup-top-row">
                    <div className="flex items-center gap-2.5" data-testid="signup-brand">
                        {brand.logoUrl ? (
                            <img src={brand.logoUrl} alt={brand.configuredName || "Brand logo"}
                                className="h-10 w-auto max-w-[180px] object-contain" data-testid="signup-brand-logo" />
                        ) : (
                            <>
                                <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-md" data-testid="signup-brand-mark">
                                    <span className="font-mono text-primary-foreground font-semibold">{(brand.configuredName || brand.appName).charAt(0).toUpperCase()}</span>
                                </div>
                                {brand.configuredName && (
                                    <span className="text-lg font-semibold tracking-tight" data-testid="signup-brand-name">{brand.configuredName}</span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="text-sm text-muted-foreground" data-testid="signup-already-have-account">
                        Already have an account? <Link to="/login" className="text-primary underline font-medium hover:text-primary/80" data-testid="signup-signin-link">Sign in</Link>
                    </div>
                </div>

                <div className="max-w-xl w-full mx-auto">
                    {policy.trial_period_days && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-[10px] font-semibold tracking-wider uppercase mb-4" data-testid="signup-trial-badge">
                            <Gift className="h-3 w-3" />
                            {policy.trial_period_days}-Day Free Trial
                        </div>
                    )}
                    <h1 className="text-4xl font-semibold tracking-tight leading-[1.05] mb-6" data-testid="signup-heading">
                        {policy.trial_period_days ? (
                            <>Try {brand.appName} <span className="font-serif-display italic font-normal text-primary">free</span> for {policy.trial_period_days} days</>
                        ) : (
                            <>Get started with {brand.appName}</>
                        )}
                    </h1>
                    <SignupForm
                        name={name} setName={setName}
                        email={email} setEmail={setEmail}
                        setUserTouchedEmail={setUserTouchedEmail}
                        password={password} setPassword={setPassword}
                        showPassword={showPassword} setShowPassword={setShowPassword}
                        contact={contact} setContact={setContact}
                        countryCode={countryCode} setCountryCode={setCountryCode}
                        industry={industry} setIndustry={setIndustry}
                        industries={industries}
                        customIndustry={customIndustry} setCustomIndustry={setCustomIndustry}
                        emailDomainOk={emailDomainOk}
                        emailDomainReason={emailDomainReason}
                        emailChecking={emailChecking}
                        legalLinks={legalLinks}
                        orgName={orgName} setOrgName={setOrgName}
                        orgNameLocked={orgNameLocked}
                        tenantName={tenantName} setTenantName={setTenantName}
                        tenantPlaceholder={tenantPlaceholder}
                        acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms}
                        tosModalOpen={tosModalOpen} setTosModalOpen={setTosModalOpen}
                        tosVersionId={tosVersionId} setTosVersionId={setTosVersionId}
                        policy={policy} pwState={pwState}
                        referrerUserId={referrerUserId}
                        onCaptchaToken={onCaptchaToken}
                        captchaToken={captchaToken}
                        serverError={serverError}
                        onSubmit={initiate}
                        canSubmit={canSubmit}
                        submitting={submitting}
                        brand={brand}
                        /* iter-2026 — merged single-screen OTP props */
                        stage={stage}
                        otpCode={otpCode} setOtpCode={setOtpCode}
                        otpError={otpError} setOtpError={setOtpError}
                        otpCooldown={otpCooldown}
                        demoCode={demoCode}
                        verifying={verifying}
                        onVerify={verify}
                        onResend={resendOtp}
                        canVerify={canVerify}
                    />

                    {/* iter-121.102 — Single-line trust strip: each
                        tile is icon + ONE short caption (combined
                        label + sub into one phrase) with whitespace-
                        nowrap so the row never wraps even at
                        tablet widths. */}
                    <div className="mt-4 grid grid-cols-3 gap-2 p-4 rounded-md bg-primary/[0.05] border border-primary/15" data-testid="signup-trust-strip">
                        {[
                            { label: "No credit card required", Icon: CreditCard },
                            { label: "Secure & trusted", Icon: ShieldCheck },
                            { label: "Setup in 2 min", Icon: Zap },
                        ].filter(Boolean).map((t) => (
                            <div key={t.label} className="flex items-center gap-2 min-w-0" data-testid={`signup-trust-tile-${t.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                                <div className="h-8 w-8 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                                    <t.Icon className="h-4 w-4" strokeWidth={1.75} />
                                </div>
                                <div className="text-[12px] font-medium leading-tight whitespace-nowrap">{t.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right: hero — iter-121.100 — uploaded signup_image_url overrides
                (falls back to login_image_url for backwards-compat); otherwise
                renders a CSS-only mock matching the user-supplied screenshot
                (floating dashboard cards: Revenue / Users / Top Channels donut
                + "Spin up workspaces in 90 seconds" + bullet list + 19+/500+/
                99.95% trust tiles). */}
            <div className={`hidden lg:flex relative overflow-hidden flex-col ${(brand.signupImageUrl || !brandResolved) ? "bg-muted" : "bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950"}`}
                 data-testid="signup-hero-panel"
                 data-hero-state={brand.signupImageUrl ? "image" : (brandResolved ? "default" : "loading")}>
                {brand.signupImageUrl ? (
                    <BrandHeroImage src={brand.signupImageUrl} testId="signup-hero-image" />
                ) : !brandResolved ? (
                    /* Branding still loading → neutral panel (no gradient flash) */
                    <div className="absolute inset-0 bg-muted" data-testid="signup-hero-loading" />
                ) : (
                    <>
                        {/* Floating mock cards mirroring the screenshot */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                            {/* Revenue card */}
                            <div className="absolute top-[8%] left-[8%] w-[230px] rounded-2xl bg-gradient-to-br from-white/[0.16] to-white/[0.04] border border-white/15 backdrop-blur-md shadow-2xl p-4 rotate-[-3deg]">
                                <div className="text-[11px] text-white/70">Revenue</div>
                                <div className="text-2xl font-semibold text-white mt-1">$847K <span className="text-emerald-400 text-[10px] font-normal align-middle ml-1">▲ 12.5%</span></div>
                                <svg viewBox="0 0 200 40" className="w-full h-7 mt-2" preserveAspectRatio="none">
                                    <path d="M0,32 C30,22 50,28 80,16 C110,8 140,22 170,8 L200,4" stroke="#a78bfa" strokeWidth="2" fill="none" />
                                </svg>
                            </div>
                            {/* Users card */}
                            <div className="absolute top-[5%] right-[8%] w-[230px] rounded-2xl bg-gradient-to-br from-white/[0.16] to-white/[0.04] border border-white/15 backdrop-blur-md shadow-2xl p-4 rotate-[2deg]">
                                <div className="text-[11px] text-white/70">Users</div>
                                <div className="text-2xl font-semibold text-white mt-1">12.8K <span className="text-emerald-400 text-[10px] font-normal align-middle ml-1">▲ 8.3%</span></div>
                                <svg viewBox="0 0 200 40" className="w-full h-7 mt-2" preserveAspectRatio="none">
                                    <path d="M0,30 C30,20 50,26 80,18 C110,12 140,16 170,8 L200,6" stroke="#60a5fa" strokeWidth="2" fill="none" />
                                </svg>
                            </div>
                            {/* Top Channels donut card */}
                            <div className="absolute top-[34%] left-[18%] w-[300px] rounded-2xl bg-gradient-to-br from-white/[0.14] to-white/[0.03] border border-white/15 backdrop-blur-md shadow-2xl p-4 rotate-[1deg]">
                                <div className="text-[11px] text-white/70 mb-3">Top Channels</div>
                                <div className="flex items-center gap-4">
                                    <div className="relative w-20 h-20 shrink-0">
                                        <div className="w-full h-full rounded-full border-[10px] border-violet-300/40 border-l-violet-400/90 border-t-violet-400/90" />
                                    </div>
                                    <div className="flex-1 space-y-1.5 text-[10px] text-white/85">
                                        <div className="flex justify-between"><span><span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 mr-1.5" />Organic Search</span><span>48%</span></div>
                                        <div className="flex justify-between"><span><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mr-1.5" />Direct</span><span>27%</span></div>
                                        <div className="flex justify-between"><span><span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 mr-1.5" />Paid Search</span><span>15%</span></div>
                                        <div className="flex justify-between"><span><span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-400 mr-1.5" />Social</span><span>10%</span></div>
                                    </div>
                                </div>
                            </div>
                            {/* Decorative glows + dot grid */}
                            <div className="absolute right-[5%] top-[35%] w-[50%] h-[40%] rounded-full bg-violet-500/20 blur-3xl" />
                            <div className="absolute left-[-5%] bottom-[5%] w-[40%] h-[30%] rounded-full bg-indigo-500/15 blur-3xl" />
                        </div>

                        <div className="relative z-0 mt-auto p-12 text-white">
                            <h2 className="text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05] max-w-lg mb-5" data-testid="signup-hero-heading">
                                Spin up <span className="font-serif-display italic font-normal">analytics</span>
                                <br />workspaces in under 90 seconds.
                            </h2>
                            <ul className="space-y-3 max-w-md text-sm text-white/90 mb-6" data-testid="signup-hero-bullets">
                                {[
                                    "Bring CSVs, Google Ads, Meta Ads, SQL — 19+ connectors.",
                                    "Ask Sense AI in plain English. Get charts back instantly.",
                                    "Share dashboards across your org with row-level security.",
                                    policy.trial_period_days ? `${policy.trial_period_days} days free. Cancel anytime. No card required.` : "No credit card required. Cancel anytime.",
                                ].map((t, i) => (
                                    <li key={i} className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-white" /><span>{t}</span></li>
                                ))}
                            </ul>
                            <div className="grid grid-cols-3 gap-6 max-w-md pt-5 border-t border-white/20">
                                <div className="flex items-start gap-2.5"><div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><UsersIcon className="h-4 w-4" /></div><div><div className="kpi-number text-xl text-white">19+</div><div className="text-[10px] uppercase tracking-widest text-white/60">Connectors</div></div></div>
                                <div className="flex items-start gap-2.5"><div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><Building2 className="h-4 w-4" /></div><div><div className="kpi-number text-xl text-white">500+</div><div className="text-[10px] uppercase tracking-widest text-white/60">Enterprises</div></div></div>
                                <div className="flex items-start gap-2.5"><div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><ShieldCheck className="h-4 w-4" /></div><div><div className="kpi-number text-xl text-white">99.95%</div><div className="text-[10px] uppercase tracking-widest text-white/60">Uptime SLA</div></div></div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Step 1: signup form ────────────────────────────────────────────────────
function SignupForm({
    name, setName, email, setEmail, setUserTouchedEmail, password, setPassword, showPassword, setShowPassword,
    contact, setContact, countryCode, setCountryCode, industry, setIndustry, industries,
    customIndustry, setCustomIndustry,
    emailDomainOk, emailDomainReason, emailChecking,
    legalLinks,
    orgName, setOrgName, orgNameLocked, tenantName, setTenantName, tenantPlaceholder,
    acceptedTerms, setAcceptedTerms,
    tosModalOpen, setTosModalOpen, tosVersionId, setTosVersionId,
    policy, pwState, referrerUserId,
    onCaptchaToken, captchaToken, serverError, onSubmit, canSubmit, submitting, brand,
    // iter-2026 — merged single-screen OTP props
    stage, otpCode, setOtpCode, otpError, setOtpError, otpCooldown, demoCode, verifying, onVerify, onResend, canVerify,
}) {
    const otpSent = stage === "otp";
    return (
        <TooltipProvider delayDuration={150}>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4" data-testid="signup-form">
            {/* iter-2026 — HIDDEN profile fields. The single-screen signup UI
                collects only email + password. Name, country, contact number,
                industry and organization name are kept in the DOM (so nothing
                is removed and QA can still trace them) but hidden; they are
                optional server-side (stored NULL / org auto-derived from the
                email domain). */}
            <input type="hidden" value={contact} readOnly data-testid="signup-contact-input" />
            <input type="hidden" value={countryCode} readOnly data-testid="signup-country-input" />
            <input type="hidden" value={industry} readOnly data-testid="signup-industry-input" />
            <input type="hidden" value={customIndustry} readOnly data-testid="signup-custom-industry-input" />
            <input type="hidden" value={orgName} readOnly data-testid="signup-org-name-input" />
            <input type="hidden" value={tenantName} readOnly data-testid="signup-tenant-name-input" />

            {/* Name — optional. When left blank the backend auto-derives it
                from the email local-part (text before "@"). */}
            <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                    <label className="eyebrow leading-none">Name</label>
                    <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">(optional)</span>
                </div>
                <div className="relative">
                    <UserIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input type="text" autoComplete="name"
                        value={name} onChange={(e) => setName(e.target.value)}
                        className="w-full h-10 pl-8 pr-3 bg-transparent border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="signup-name-input" placeholder="Your name" />
                </div>
            </div>

            <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                    <label className="eyebrow leading-none">Work email <span className="text-destructive" aria-hidden="true">*</span></label>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button" className="inline-flex h-4 w-5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/20 transition-colors" aria-label="Work email help">
                                <Info className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px] text-xs">
                            Use your work email — personal providers (Gmail, Yahoo, etc.) are not accepted.
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="relative">
                    <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input type="email" required autoComplete="email"
                        value={email} onChange={(e) => { setEmail(e.target.value); setUserTouchedEmail(true); }}
                        className={
                            "w-full h-10 pl-8 pr-3 bg-transparent border rounded-sm text-sm font-mono focus:outline-none focus:ring-1 "
                            + (emailDomainOk === false
                                ? "border-destructive focus:ring-destructive"
                                : "border-border focus:ring-primary")
                        }
                        data-testid="signup-email-input" placeholder="you@company.com" />
                </div>
                {emailDomainOk === false ? (
                    <div
                        className="text-[10px] text-destructive mt-1 flex items-start gap-1"
                        data-testid="signup-email-domain-error"
                    >
                        <span aria-hidden>•</span>
                        <span>{emailDomainReason || "This email domain is not allowed."}</span>
                    </div>
                ) : emailChecking ? (
                    <div className="text-[10px] text-muted-foreground mt-1" data-testid="signup-email-checking">
                        Checking your work email…
                    </div>
                ) : null}
            </div>

            <div>
                <div className="flex items-center gap-2 mb-1.5">
                    <label className="eyebrow leading-none">Password <span className="text-destructive" aria-hidden="true">*</span></label>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/20 transition-colors" aria-label="Password requirements">
                                <Info className="h-4 w-4" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                            <p className="mb-1 font-medium">Your password needs:</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                                <li>At least {policy.password_min_length} characters</li>
                                <li>At least {policy.password_required_classes} of: lowercase, uppercase, digit, symbol</li>
                                <li>Different from your email address</li>
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="relative">
                    <input type={showPassword ? "text" : "password"} required autoComplete="new-password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-10 px-3 pr-10 bg-transparent border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                        data-testid="signup-password-input" placeholder="Strong, unique, memorable" />
                    <button type="button" onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="signup-show-password">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                {password && (
                    <>
                        <PasswordStrengthMeter state={pwState} requiredClasses={policy.password_required_classes} />
                        {!pwState.strongEnough && (
                            <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5" data-testid="signup-password-hint">
                                Password doesn&apos;t meet the requirements yet — finish the unmet items above to enable &ldquo;Send verification code&rdquo;.
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* iter-2026 — Verify-your-email card (merged into the single screen). */}
            <div className="rounded-md border border-border p-4 bg-muted/[0.15]" data-testid="signup-verify-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                            <MailCheck className="h-4 w-4 text-primary" /> Verify your email
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                            Enter the 6-digit code we email you to finish creating your workspace.
                        </div>
                    </div>
                    <Button type="button" size="sm"
                        onClick={onSubmit} disabled={!canSubmit || otpSent}
                        className="rounded-sm shrink-0 h-9"
                        data-testid="signup-send-code-button">
                        {submitting
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                            : otpSent ? "Code sent" : "Send verification code"}
                    </Button>
                </div>
                {demoCode && (
                    <div className="text-[11px] font-mono text-muted-foreground p-2 mb-3 bg-secondary/30 border border-dashed border-border rounded-sm" data-testid="signup-otp-demo-hint">
                        SMTP off · demo code: <span className="text-primary">{demoCode}</span>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <OtpBoxes value={otpCode} onChange={(v) => { setOtpCode(v); if (otpError) setOtpError(""); }} onEnter={onVerify} disabled={!otpSent} />
                    <button type="button" onClick={onResend} disabled={!otpSent || otpCooldown > 0}
                        className="text-[11px] text-primary hover:text-primary/80 disabled:text-muted-foreground/40 flex items-center gap-1"
                        data-testid="signup-otp-resend">
                        <RefreshCw className="h-3 w-3" />
                        {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend code"}
                    </button>
                </div>
                {otpError && (
                    <div className="mt-2 text-[11px] text-destructive flex items-start gap-1" data-testid="signup-otp-error">
                        <span aria-hidden>•</span>
                        <span>{otpError}</span>
                    </div>
                )}
            </div>

            {referrerUserId && (
                <div className="flex items-center gap-2 px-3 py-2 border border-primary/40 bg-primary/5 rounded-sm text-[11px]" data-testid="signup-referral-banner">
                    <Gift className="h-3.5 w-3.5 text-primary" />
                    <span>You're starting a referred trial. Welcome aboard!</span>
                </div>
            )}

            {/* Captcha widget — auto-renders when SA has configured it */}
            {policy.captcha_enabled && policy.captcha_site_key && (
                <div data-testid="signup-captcha-section">
                    <label className="eyebrow mb-1.5 block">Verify you're human</label>
                    <CaptchaWidget
                        provider={policy.captcha_provider}
                        siteKey={policy.captcha_site_key}
                        onToken={onCaptchaToken}
                    />
                    {!captchaToken && (
                        <div className="text-[10px] text-muted-foreground mt-1">Complete the challenge above to continue.</div>
                    )}
                </div>
            )}

            {serverError && (
                <div className="text-[12px] text-primary px-3 py-2 border border-primary/40 rounded-sm bg-primary/5 leading-snug" data-testid="signup-server-error">
                    {serverError}
                </div>
            )}

            <Button type="button" onClick={onVerify} disabled={!canVerify} className="w-full h-11 rounded-sm font-medium mt-2" data-testid="signup-submit-button">
                {verifying
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</>
                    : <>Verify &amp; get started <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
        </form>
        </TooltipProvider>
    );
}

// ─── 6-box OTP input (merged single-screen signup) ──────────────────────────
function OtpBoxes({ value, onChange, onEnter, disabled }) {
    const refs = useRef([]);
    const chars = Array.from({ length: 6 }, (_, i) => (value || "")[i] || "");
    const setAt = (i, raw) => {
        const digits = (raw || "").replace(/\D/g, "");
        if (!digits) {
            const arr = (value || "").split("");
            arr[i] = "";
            onChange(arr.join("").replace(/\s/g, "").slice(0, 6));
            return;
        }
        const next = Array.from({ length: 6 }, (_, k) => (value || "")[k] || "");
        let idx = i;
        for (const d of digits.split("")) {
            if (idx > 5) break;
            next[idx] = d;
            idx++;
        }
        onChange(next.join("").replace(/\s/g, "").slice(0, 6));
        refs.current[Math.min(5, i + digits.length)]?.focus();
    };
    const onKeyDown = (i, e) => {
        if (e.key === "Enter") { onEnter?.(); return; }
        if (e.key === "Backspace" && !chars[i] && i > 0) refs.current[i - 1]?.focus();
    };
    const onPaste = (e) => {
        e.preventDefault();
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
        if (pasted) { onChange(pasted); refs.current[Math.min(5, pasted.length - 1)]?.focus(); }
    };
    return (
        <div className="flex items-center gap-2" data-testid="signup-otp-boxes" onPaste={onPaste}>
            {chars.map((c, i) => (
                <input
                    key={i}
                    ref={(el) => (refs.current[i] = el)}
                    inputMode="numeric"
                    maxLength={1}
                    value={c}
                    disabled={disabled}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setAt(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    className="w-11 h-12 bg-background border border-border rounded-sm text-lg text-center font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`signup-otp-box-${i}`}
                    aria-label={`Verification code digit ${i + 1}`}
                />
            ))}
        </div>
    );
}

function PasswordStrengthMeter({ state, requiredClasses }) {
    const lenOk = state.checks.find((c) => c.id === "len")?.ok;
    const strong = state.strongEnough;
    const meterStops = 5;
    const filled = Math.min(meterStops, [state.classCount, lenOk ? 1 : 0].reduce((a, b) => a + b, 0));
    return (
        <div className="mt-2" data-testid="signup-password-strength">
            <div className="flex gap-1 mb-2">
                {Array.from({ length: meterStops }).map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < filled ? strong ? "bg-emerald-500" : "bg-amber-500" : "bg-secondary"
                        }`} />
                ))}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
                {state.checks.map((c) => (
                    <div key={c.id} data-testid={`signup-pw-check-${c.id}`}
                        className={`flex items-center gap-1 ${c.ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        {c.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>{c.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
